import { AdminEmailEditor } from '../../../components/admin/AdminEmailEditor';
import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  FormLabel,
  Divider,
  Input,
  LinearProgress,
  Option,
  Select,
  Sheet,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  GroupsOutlined,
  LinkOutlined,
  ChecklistOutlined,
  MailOutline,
  SmsOutlined,
  CreditCardOutlined,
} from '@mui/icons-material';
import { AccountSection } from '../accounts/AccountSection';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminCXAPI, cxError } from '../../../lib/apiCX';
import { AdminStaffAPI } from '../../../lib/apiAdmin';
import { StaffPicker } from '../../../components/admin/StaffPicker';
import type { CXConfig, CXSettings } from '../../../types/cx';
import { MessagePreview, TemplateFieldGuide } from './MessagePersonalization';

const sectionSx = {
  p: 2.5,
  borderRadius: 'md',
  display: 'grid',
  gap: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.level1',
};
const gridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  gap: 1.5,
};
const textExamples: Record<string, { when: string; body: string }> = {
  introduction: {
    when: 'After a new inquiry, to acknowledge it and learn what they need.',
    body: 'Hi {{first_name}}, thanks for reaching out to ApexOS! What would you most like help with at {{company_name}}?',
  },
  team: {
    when: 'When they are ready to bring teammates into the account. Confirm this is still needed first.',
    body: 'Hi {{first_name}}, ready to get your team set up in ApexOS? We can walk through invitations and access together. Would a quick screenshare help?',
  },
  mobile_app: {
    when: 'When field users are ready to try the mobile app. Skip this if they are already using it.',
    body: 'Hi {{first_name}}, would it help to walk through the ApexOS mobile app with your field team? What day works for a quick screenshare?',
  },
};
function TextField({
  label,
  value,
  onChange,
  multiline = false,
  type = 'text',
  email = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  type?: string;
  email?: boolean;
}) {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      {email ? (
        <AdminEmailEditor label={label} text={/<\/?[a-z][^>]*>/i.test(value) ? '' : value}
          html={/<\/?[a-z][^>]*>/i.test(value) ? value : ''} onChange={html => onChange(html)} />
      ) : multiline ? (
        <Textarea minRows={3} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </FormControl>
  );
}

function SettingsEditor({ saved, onSaved }: { saved: CXSettings; onSaved: (data: CXSettings) => void }) {
  const [config, setConfig] = useState(saved.config);
  const [staffId, setStaffId] = useState<number | null>(null);
  const [followupText, setFollowupText] = useState(saved.config.followup_days.join(', '));
  const platformEmail = saved.welcome_delivery?.default_from_email || '';
  const senderDomains = saved.welcome_delivery?.allowed_sender_domains || [];
  const senderDomain = config.sender_email.trim().split('@')[1]?.toLowerCase();
  const senderMismatch = !!senderDomain && senderDomains.length > 0 && !senderDomains.includes(senderDomain);
  const staff = useQuery({
    queryKey: ['admin', 'staff'],
    queryFn: () => AdminStaffAPI.list(),
  });
  const update = <K extends keyof CXConfig>(key: K, value: CXConfig[K]) =>
    setConfig((current) => ({ ...current, [key]: value }));
  const senders = useQuery({
    queryKey: ['admin', 'cx', 'sms-senders', config.auto_sms_sender_user_id],
    queryFn: () => AdminCXAPI.smsSenders(config.auto_sms_sender_user_id!),
    enabled: !!config.auto_sms_sender_user_id,
  });
  const save = useMutation({
    mutationFn: () =>
      AdminCXAPI.saveSettings({
        config: {
          ...config,
          followup_days: followupText
            .split(',')
            .filter((part) => part.trim())
            .map((part) => Number(part.trim())),
          migration_sources: config.migration_sources.map((value) => value.trim()).filter(Boolean),
        },
        revision: saved.revision,
      }),
    onSuccess: onSaved,
  });
  const textField = (key: keyof CXConfig, label: string, multiline = false, type = 'text') => (
    <TextField
      key={key}
      label={label}
      value={String(config[key])}
      onChange={(value) => update(key, value as never)}
      multiline={multiline}
      type={type}
    />
  );
  const numericField = (
    key: 'first_call_minutes' | 'sms_weekly_limit' | 'sms_spacing_hours',
    label: string,
  ) => (
    <TextField
      key={key}
      label={label}
      type="number"
      value={String(config[key])}
      onChange={(value) => update(key, Number(value))}
    />
  );
  const moveStep = (index: number, offset: number) => {
    const steps = [...config.steps];
    [steps[index], steps[index + offset]] = [steps[index + offset], steps[index]];
    update('steps', steps);
  };
  const previewConfig = {
    sender_name: config.sender_name,
    setup_booking_url: config.setup_booking_url,
    marketing_booking_url: config.marketing_booking_url,
    ios_app_url: config.ios_app_url,
    android_app_url: config.android_app_url,
    staff: config.staff,
  };
  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Typography component="h2" level="h3">
            Onboarding playbook
          </Typography>
          <Typography level="body-sm">
            Set the shared defaults your Customer Success team uses for every account. Update an individual
            customer’s progress in their account profile.
          </Typography>
        </div>
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Save settings
        </Button>
      </Box>
      {save.error && <Alert color="danger">{cxError(save.error)}</Alert>}
      <Alert color="neutral">
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Typography level="body-sm">
            Welcome emails and trial texts have separate sending controls. Choose a section below to review
            its sender and turn it on, then select Save settings. Customer text replies alert your team and
            pause that account’s automatic follow-ups.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              component="a"
              href="#welcome-emails"
              size="sm"
              variant="outlined"
              startDecorator={<MailOutline />}
            >
              Welcome emails · {saved.config.welcome_email_enabled ? 'On' : 'Off'}
            </Button>
            <Button
              component="a"
              href="#automatic-texts"
              size="sm"
              variant="outlined"
              startDecorator={<SmsOutlined />}
            >
              Automatic texts · {saved.config.auto_sms_enabled ? 'On' : 'Off'}
            </Button>
          </Box>
        </Box>
      </Alert>
      <AccountSection
        title="Assignment & team coverage"
        description="Choose who receives new inquiries and when your team is available. CSM means Customer Success Manager."
        icon={<GroupsOutlined />}
        help="Only checked teammates accept new leads. Coverage hours and days determine when the first-call task is due."
      >
        <Checkbox
          label="Assign new inquiries randomly from this pool"
          checked={config.assignment_enabled}
          onChange={(e) => update('assignment_enabled', e.target.checked)}
        />
        {!config.staff.length && (
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            No teammates in the pool yet. Choose a Customer Success Manager below to get started.
          </Typography>
        )}
        {config.staff.map((member, index) => (
          <Sheet
            variant="soft"
            key={member.user_id}
            sx={{ ...gridSx, alignItems: 'center', p: 2, borderRadius: 'md' }}
          >
            <Checkbox
              label={
                staff.data?.results.find((person) => person.user_id === member.user_id)?.name ||
                `Staff user ${member.user_id}`
              }
              checked={member.accepts_new_leads}
              onChange={(e) =>
                update(
                  'staff',
                  config.staff.map((row, i) =>
                    i === index ? { ...row, accepts_new_leads: e.target.checked } : row,
                  ),
                )
              }
            />
            <TextField
              label="CSM setup booking link"
              type="url"
              value={member.setup_booking_url}
              onChange={(value) =>
                update(
                  'staff',
                  config.staff.map((row, i) => (i === index ? { ...row, setup_booking_url: value } : row)),
                )
              }
            />
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() =>
                update(
                  'staff',
                  config.staff.filter((_, i) => i !== index),
                )
              }
            >
              Remove from pool
            </Button>
          </Sheet>
        ))}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <StaffPicker value={staffId} onChange={setStaffId} placeholder="Choose a CSM" />
          <Button
            variant="outlined"
            disabled={!staffId || config.staff.some((row) => row.user_id === staffId)}
            onClick={() => {
              if (staffId)
                update('staff', [
                  ...config.staff,
                  {
                    user_id: staffId,
                    accepts_new_leads: true,
                    setup_booking_url: '',
                  },
                ]);
              setStaffId(null);
            }}
          >
            Add CSM
          </Button>
        </Box>
        <FormControl>
          <FormLabel>Backup for unassigned inquiries</FormLabel>
          <StaffPicker value={config.backup_user_id} onChange={(value) => update('backup_user_id', value)} />
        </FormControl>
        <Divider />
        <Box>
          <Typography level="title-md">Coverage hours</Typography>
          <Typography level="body-sm" sx={{ mt: 0.5, color: 'text.secondary' }}>
            The first-call countdown and automatic texts use these working days and hours, in the timezone
            below.
          </Typography>
        </Box>
        <Box sx={gridSx}>
          {textField('timezone', 'Coverage timezone')}
          {numericField('first_call_minutes', 'First call due within staffed minutes')}
          {textField('workday_start', 'Coverage starts', false, 'time')}
          {textField('workday_end', 'Coverage ends', false, 'time')}
        </Box>
        <Box role="group" aria-label="Working days">
          <Typography level="title-sm" sx={{ mb: 1.5 }}>
            Working days
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(
              (day, index) => (
                <Checkbox
                  key={day}
                  label={day}
                  checked={config.working_days.includes(index)}
                  onChange={(e) =>
                    update(
                      'working_days',
                      e.target.checked
                        ? [...config.working_days, index]
                        : config.working_days.filter((value) => value !== index),
                    )
                  }
                />
              ),
            )}
          </Box>
        </Box>
      </AccountSection>
      <AccountSection
        title="Booking links & email sender"
        description="Keep customer-facing links and the onboarding sender details together."
        icon={<LinkOutlined />}
        help="A teammate’s setup booking link takes priority over the default when available. These sender settings do not change personal emails sent from Sales or Accounts."
      >
        <Box>
          <Typography level="title-md" sx={{ mb: 1.5 }}>
            Schedule a call
          </Typography>
          <Box sx={gridSx}>
            {textField('setup_booking_url', 'Default setup-call link', false, 'url')}
            {textField('marketing_booking_url', 'Marketing-call link', false, 'url')}
          </Box>
        </Box>
        <Divider />
        <Box>
          <Typography level="title-md">Onboarding email sender</Typography>
          <Typography level="body-sm" sx={{ mt: 0.5, mb: 1.5 }}>
            Welcome emails use ApexOS’s existing Mailgun connection. Choose the name customers see and
            the inbox where their replies should go.
          </Typography>
          {platformEmail && (
            <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
              <Typography level="title-sm">Platform Mailgun sender: {platformEmail}</Typography>
              <Typography level="body-sm">
                Leave Welcome sender email blank to use this address. Reply-to can be your personal or
                team inbox, even on a different domain.
              </Typography>
            </Sheet>
          )}
          <Box sx={gridSx}>
            {textField('sender_name', 'Welcome sender name')}
            <FormControl error={senderMismatch}>
              <FormLabel>Welcome sender email</FormLabel>
              <Input type="email" value={config.sender_email} placeholder={platformEmail || 'Use platform default'}
                onChange={(event) => update('sender_email', event.target.value)} />
              <FormHelperText sx={{ color: senderMismatch ? 'danger.softColor' : 'text.tertiary' }}>
                {senderMismatch ? 'This address uses a different sending domain. ' : 'Optional. '}
                {senderDomains.length > 0
                  ? `Custom addresses must use ${senderDomains.map((domain) => `@${domain}`).join(' or ')}.`
                  : 'Leave blank to use the platform Mailgun sender.'}
              </FormHelperText>
              {platformEmail && config.sender_email.trim() && (
                <Button variant="outlined" size="sm" sx={{ mt: 1, alignSelf: 'flex-start' }}
                  onClick={() => update('sender_email', '')}>
                  Use platform sender
                </Button>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Reply-to email</FormLabel>
              <Input type="email" value={config.reply_to_email}
                onChange={(event) => update('reply_to_email', event.target.value)} />
              <FormHelperText>Required for automatic welcome emails. Customer replies go to this inbox.</FormHelperText>
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box>
          <Typography level="title-md" sx={{ mb: 1.5 }}>
            Mobile app downloads
          </Typography>
          <Box sx={gridSx}>
            {textField('ios_app_url', 'iPhone / iPad app link', false, 'url')}
            {textField('android_app_url', 'Android app link', false, 'url')}
          </Box>
        </Box>
      </AccountSection>
      <AccountSection
        title="Onboarding steps & resources"
        description="Define the steps and reference materials shown in each account’s Onboarding tab."
        icon={<ChecklistOutlined />}
        help="In-sequence steps determine the next recommended step. Throughout-onboarding steps can be worked on at any time. Disabling a step hides it from the active plan."
      >
        <Typography level="body-sm">
          Work through in-sequence steps in order. Team and mobile steps can run alongside them.
        </Typography>
        <TextField
          label="Migration source systems (one per line)"
          value={config.migration_sources.join('\n')}
          multiline
          onChange={(value) => update('migration_sources', value.split('\n'))}
        />
        <AccordionGroup
          sx={{
            gap: 1.5,
            '& .MuiAccordion-root': {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 'md',
              overflow: 'hidden',
            },
            '& .MuiAccordionSummary-root': { bgcolor: 'background.level1', minHeight: 56 },
            '& .MuiAccordionDetails-content': { pt: 2 },
          }}
        >
          {config.steps.map((step, index) => (
            <Accordion key={step.key}>
              <AccordionSummary>
                <Typography level="title-sm">
                  {index + 1}. {step.title} · {step.enabled ? 'Enabled' : 'Disabled'}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'grid', gap: 1.5, pb: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    <Checkbox
                      label="Enabled"
                      checked={step.enabled}
                      onChange={(e) =>
                        update(
                          'steps',
                          config.steps.map((row, i) =>
                            i === index ? { ...row, enabled: e.target.checked } : row,
                          ),
                        )
                      }
                    />
                    <Typography level="title-sm">
                      {index + 1}. {step.title}
                    </Typography>
                    <Button
                      size="sm"
                      variant="plain"
                      disabled={index === 0}
                      onClick={() => moveStep(index, -1)}
                    >
                      Move up
                    </Button>
                    <Button
                      size="sm"
                      variant="plain"
                      disabled={index === config.steps.length - 1}
                      onClick={() => moveStep(index, 1)}
                    >
                      Move down
                    </Button>
                  </Box>
                  <Box sx={gridSx}>
                    <TextField
                      label="Step name"
                      value={step.title}
                      onChange={(value) =>
                        update(
                          'steps',
                          config.steps.map((row, i) => (i === index ? { ...row, title: value } : row)),
                        )
                      }
                    />
                    <FormControl>
                      <FormLabel>When this step applies</FormLabel>
                      <Select
                        value={step.track}
                        onChange={(_, value) => {
                          if (value)
                            update(
                              'steps',
                              config.steps.map((row, i) => (i === index ? { ...row, track: value } : row)),
                            );
                        }}
                      >
                        <Option value="sequential">In sequence</Option>
                        <Option value="parallel">Throughout onboarding</Option>
                      </Select>
                    </FormControl>
                  </Box>
                  {(['guide_url', 'video_url', 'call_script', 'checklist'] as const).map((key) => (
                    <TextField
                      key={key}
                      label={
                        {
                          guide_url: 'Help article link',
                          video_url: 'Video link',
                          call_script: 'Call script',
                          checklist: 'Checklist / instructions',
                        }[key]
                      }
                      value={step[key]}
                      multiline={key === 'call_script' || key === 'checklist'}
                      onChange={(value) =>
                        update(
                          'steps',
                          config.steps.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
                        )
                      }
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionGroup>
        <Button
          variant="outlined"
          onClick={() =>
            update('steps', [
              ...config.steps,
              {
                key: `custom_${Date.now()}`,
                title: 'New step',
                track: 'sequential',
                enabled: true,
                guide_url: '',
                video_url: '',
                call_script: '',
                checklist: '',
              },
            ])
          }
        >
          Add onboarding step
        </Button>
      </AccountSection>
      <AccountSection
        id="welcome-emails"
        title="Welcome emails by company size"
        description="Send a personalized acknowledgment when a prospect submits the website inquiry form. Choose a message by team size; every send is recorded in the lead’s timeline."
        icon={<MailOutline />}
        action={
          <Chip variant="soft" color={saved.config.welcome_email_enabled ? 'success' : 'neutral'}>
            {saved.config.welcome_email_enabled ? 'Saved: welcome emails on' : 'Saved: welcome emails off'}
          </Chip>
        }
      >
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', display: 'grid', gap: 1.5 }}>
          <Checkbox
            label="Send welcome emails automatically for new inquiries"
            checked={!!config.welcome_email_enabled}
            onChange={(event) => update('welcome_email_enabled', event.target.checked)}
          />
          {!!config.welcome_email_enabled !== !!saved.config.welcome_email_enabled && (
            <Typography level="body-sm" color="warning">
              Unsaved change — select Save settings to apply this sending mode.
            </Typography>
          )}
          <Typography level="body-sm">
            When on, a new website inquiry queues one welcome email immediately, even outside working hours.
            Repeat submissions do not send another welcome. Imported leads, manually added leads, spam, and
            customer support inquiries are excluded. Turning this on does not email older inquiries.
          </Typography>
          <Typography level="body-sm">
            The saved wording is used as written, with personalization fields filled in. AI does not rewrite
            these welcome emails. HTML formatting is supported, with a plain-text version included.
          </Typography>
          {saved.welcome_delivery && (
            <Typography level="body-sm">
              Sender after saving: {config.sender_email.trim() || platformEmail || 'Platform default'} · Replies go to:{' '}
              {config.reply_to_email.trim() || 'Set a reply-to email above'}
            </Typography>
          )}
          {!!saved.welcome_delivery?.issues.length && (
            <Alert color="warning" variant="soft">
              <Box>
                <Typography level="title-sm">Before enabling welcome emails</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {saved.welcome_delivery.issues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </Box>
                <Typography level="body-xs">
                  Based on saved settings. Complete the fields and save to check again.
                </Typography>
              </Box>
            </Alert>
          )}
          <Typography level="body-xs">
            Messages use the platform email connection. Check the lead’s timeline for sending status:
            queued, accepted, not sent, or needs review. Accepted means the email provider received the
            message; inbox delivery is not confirmed.
          </Typography>
        </Sheet>
        <TemplateFieldGuide />
        {config.welcome_segments.map((segment, index) => (
          <Sheet variant="soft" key={segment.key} sx={sectionSx}>
            <Box>
              <Typography component="h4" level="title-md">
                {segment.label || `Company size group ${index + 1}`}
              </Typography>
              <Typography level="body-sm" sx={{ mt: 0.5 }}>
                Welcome message for {segment.min_size}
                {segment.max_size === null ? '+' : `–${segment.max_size}`} people.
              </Typography>
            </Box>
            {(['label', 'subject', 'body'] as const).map((key) => (
              <TextField
                key={key}
                label={
                  {
                    label: 'Segment name',
                    subject: 'Email subject',
                    body: 'Email content',
                  }[key]
                }
                value={segment[key]}
                multiline={key === 'body'}
                email={key === 'body'}
                onChange={(value) =>
                  update(
                    'welcome_segments',
                    config.welcome_segments.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
                  )
                }
              />
            ))}
            <MessagePreview
              title={segment.label || 'Welcome email'}
              subject={segment.subject}
              body={/<\/?[a-z][^>]*>/i.test(segment.body) ? '' : segment.body}
              body_html={/<\/?[a-z][^>]*>/i.test(segment.body) ? segment.body : ''}
              config={previewConfig}
            />
            <Box sx={gridSx}>
              <TextField
                label="Minimum company size"
                type="number"
                value={String(segment.min_size)}
                onChange={(value) =>
                  update(
                    'welcome_segments',
                    config.welcome_segments.map((row, i) =>
                      i === index ? { ...row, min_size: Number(value) } : row,
                    ),
                  )
                }
              />
              <TextField
                label="Maximum company size (blank for no maximum)"
                type="number"
                value={segment.max_size === null ? '' : String(segment.max_size)}
                onChange={(value) =>
                  update(
                    'welcome_segments',
                    config.welcome_segments.map((row, i) =>
                      i === index
                        ? {
                            ...row,
                            max_size: value === '' ? null : Number(value),
                          }
                        : row,
                    ),
                  )
                }
              />
            </Box>
            <Button
              variant="plain"
              color="neutral"
              onClick={() =>
                update(
                  'welcome_segments',
                  config.welcome_segments.filter((_, i) => i !== index),
                )
              }
            >
              Remove segment
            </Button>
          </Sheet>
        ))}
        <Button
          variant="outlined"
          onClick={() =>
            update('welcome_segments', [
              ...config.welcome_segments,
              {
                key: `segment_${Date.now()}`,
                label: 'New segment',
                min_size: 1,
                max_size: 5,
                subject: '',
                body: '',
              },
            ])
          }
        >
          Add size segment
        </Button>
        <Sheet variant="soft" sx={sectionSx}>
          <Typography component="h4" level="title-md">
            When company size is unknown
          </Typography>
          <Typography level="body-sm">
            Used when team size is missing, unrecognized, or spans more than one configured size segment.
          </Typography>
          {textField('unknown_size_subject', 'Unknown company size: subject')}
          <TextField label="Unknown company size: email content" value={config.unknown_size_body} email onChange={value => update('unknown_size_body', value)} />
          <MessagePreview
            title="Unknown company size welcome email"
            subject={config.unknown_size_subject}
            body={/<\/?[a-z][^>]*>/i.test(config.unknown_size_body) ? '' : config.unknown_size_body}
            body_html={/<\/?[a-z][^>]*>/i.test(config.unknown_size_body) ? config.unknown_size_body : ''}
            config={previewConfig}
          />
        </Sheet>
      </AccountSection>
      <AccountSection
        id="automatic-texts"
        title="Onboarding texts & follow-up timing"
        description="Choose who sends trial check-ins, when they go out, and how the wording is personalized. Automatic texting is off until you enable it and save."
        icon={<SmsOutlined />}
        action={
          <Chip variant="soft" color={saved.config.auto_sms_enabled ? 'success' : 'neutral'}>
            {saved.config.auto_sms_enabled ? 'Saved: automatic texting on' : 'Saved: manual sending only'}
          </Chip>
        }
      >
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', display: 'grid', gap: 2 }}>
          <Checkbox
            label="Send onboarding texts automatically"
            checked={!!config.auto_sms_enabled}
            onChange={(event) => update('auto_sms_enabled', event.target.checked)}
          />
          {!!config.auto_sms_enabled !== !!saved.config.auto_sms_enabled && (
            <Typography level="body-sm" color="warning">
              Unsaved change — select Save settings to apply this sending mode.
            </Typography>
          )}
          <Typography level="body-sm">
            When enabled, ApexOS writes and sends a short AI check-in for eligible trial accounts without
            individual approval. It uses the saved onboarding plan, prioritizes blocked or in-progress work,
            and skips completed steps. Keep each account’s progress up to date.
          </Typography>
          {config.auto_sms_enabled && (
            <>
              <Alert color="primary" variant="soft">
                Saving starts future check-ins during your configured working hours. Existing overdue days are
                skipped. Messages use the shared ApexOS number and require documented customer permission.
                Customers’ texting registration and usage balances do not affect these check-ins.
              </Alert>
              <Box sx={gridSx}>
                <FormControl>
                  <FormLabel>Send on behalf of</FormLabel>
                  <StaffPicker
                    value={config.auto_sms_sender_user_id ?? null}
                    placeholder="Choose a platform admin"
                    onChange={(id) =>
                      setConfig((current) => ({
                        ...current,
                        auto_sms_sender_user_id: id,
                        auto_sms_from_number: '',
                      }))
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>From ApexOS</FormLabel>
                  <Input
                    aria-label="Automatic texting phone number"
                    readOnly
                    value={senders.data?.numbers[0]?.number || ''}
                    placeholder={senders.isFetching ? 'Loading ApexOS number…' : 'Shared ApexOS number'}
                  />
                  {senders.error && (
                    <Typography color="danger" level="body-sm">
                      {cxError(senders.error)}
                    </Typography>
                  )}
                  {senders.data?.numbers.length === 0 && (
                    <Typography level="body-sm">
                      {senders.data.detail || 'The shared ApexOS number needs to be configured by the platform team.'}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Typography level="body-sm">
                ApexOS covers these texts; customer usage balances are not charged. Sending hours use {config.timezone}:{' '}
                {config.workday_start}–{config.workday_end} on the working days selected above.
              </Typography>
            </>
          )}
          <Typography level="body-sm">
            Every customer reply pauses that account’s automatic texts and notifies its assigned CSM in ApexOS
            and by email. If there is no active CSM, the sending admin receives the alert; otherwise, active
            platform admins are notified. Review the conversation before resuming in the account’s Onboarding
            tab.
          </Typography>
        </Sheet>
        <TextField
          label="Trial check-in days (comma separated)"
          value={followupText}
          onChange={setFollowupText}
        />
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          Days after the trial begins. For example, 2, 4, 6 means check-ins on trial days 2, 4, and 6.
        </Typography>
        <Box sx={gridSx}>
          {numericField('sms_weekly_limit', 'Maximum proactive SMS per rolling 7 days')}
          {numericField('sms_spacing_hours', 'Minimum hours between proactive SMS')}
        </Box>
        <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', display: 'grid', gap: 0.75 }}>
          <Typography level="title-sm">How these drafts are personalized</Typography>
          <Typography level="body-sm">
            Template previews replace codes such as {'{{first_name}}'} and {'{{company_name}}'} with customer
            details. AI drafts also adapt the wording to the selected customer. Preview a customer below, or
            create a draft in the account’s Timeline & messages composer.
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Manual drafts are reviewed before sending. Automatic check-ins use the personalized onboarding
            follow-up below as a tone reference, add one relevant next step, and stop when all enabled steps
            are finished. The other templates are available for manual use.
          </Typography>
        </Sheet>
        {config.sms_templates.map((template, index) => (
          <Sheet variant="soft" key={template.key} sx={sectionSx}>
            {textExamples[template.key] && (
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography level="body-sm">{textExamples[template.key].when}</Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Example: {textExamples[template.key].body}
                </Typography>
                {!template.body && (
                  <Button
                    variant="plain"
                    size="sm"
                    sx={{ justifySelf: 'start' }}
                    onClick={() =>
                      update(
                        'sms_templates',
                        config.sms_templates.map((row, i) =>
                          i === index ? { ...row, body: textExamples[template.key].body } : row,
                        ),
                      )
                    }
                  >
                    Use this example
                  </Button>
                )}
              </Box>
            )}
            {template.key === 'setup_help' && (
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography level="title-md">Personalized onboarding follow-up</Typography>
                <Typography level="body-sm">
                  ApexOS checks each customer’s saved steps, skips completed or not-applicable work, and
                  chooses the next useful conversation. Blocked and in-progress steps take priority.
                </Typography>
                <Typography level="body-sm">
                  Example: contacts imported, proposal setup blocked → offer help with proposals. When
                  everything is complete, manual drafts can ask how things are going; automatic check-ins
                  stop. If progress is missing, ask before assuming.
                </Typography>
              </Box>
            )}
            <TextField
              label={template.key === 'setup_help' ? 'Optional wording / tone reference' : template.label}
              value={template.body}
              multiline
              onChange={(value) =>
                update(
                  'sms_templates',
                  config.sms_templates.map((row, i) => (i === index ? { ...row, body: value } : row)),
                )
              }
            />
            <MessagePreview
              title={template.label}
              body={template.body}
              config={previewConfig}
              draftMode={template.key === 'setup_help' ? 'onboarding' : 'custom'}
            />
          </Sheet>
        ))}
      </AccountSection>
      <AccountSection
        title="Billing guidance for your team"
        description="Write internal instructions for trial extensions, failed payments, and cancellation requests. Saving this guidance does not change a subscription."
        icon={<CreditCardOutlined />}
      >
        <Typography level="body-sm">
          The seven-day trial converts automatically to paid billing. Any Apex platform admin can extend an
          eligible trial through the customer plan, with Stripe confirmation and an audit record.
        </Typography>
        {textField('trial_extension_instructions', 'When to extend a trial', true)}
        {textField('payment_failure_instructions', 'Payment-failure handling', true)}
        {textField('cancellation_instructions', 'Cancellation handling', true)}
      </AccountSection>
      {save.error && <Alert color="danger">{cxError(save.error)}</Alert>}
      <Button loading={save.isPending} onClick={() => save.mutate()}>
        Save settings
      </Button>
    </Box>
  );
}

export function CXSettingsPanel() {
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'cx', 'settings'],
    queryFn: AdminCXAPI.settings,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const [notice, setNotice] = useState('');
  if (query.isPending) return <LinearProgress />;
  if (query.error || !query.data)
    return (
      <Alert color="danger">
        {cxError(query.error)}
        <Button onClick={() => query.refetch()}>Retry</Button>
      </Alert>
    );
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      {notice && <Alert color="success">{notice}</Alert>}
      <SettingsEditor
        key={query.data.revision}
        saved={query.data}
        onSaved={(data) => {
          cache.setQueryData(['admin', 'cx', 'settings'], data);
          setNotice('Settings saved.');
        }}
      />
    </Box>
  );
}
